import { usePipelineConfig } from 'features/knowledgeDocumentViewer/contexts/PipelineConfigContext'
import {
  cantileverDocumentEntryId as deriveCantileverDocumentEntryId,
  cantileverDocumentPageIdForPage,
} from 'features/knowledgeDocumentViewer/utils/cantileverHostIds'
import { useMemo } from 'react'

import { useSystemDocumentByEntryId } from './useSystemDocumentByEntryId'

export interface KnownCantileverFileHosts {
  cantileverDocumentEntryId?: string
  cantileverDocumentPageId?: string
  filename?: string
  systemDocumentEntryId?: string
  /** Bare BigQuery document_id for UUID5 page-image host derive. */
  bqDocumentId?: string
  /**
   * True when document-level hosts were taken from an already-loaded
   * system-document entry (including when the cantilever host is absent).
   */
  hasHydratedDocumentHosts?: boolean
}

/**
 * Resolve cantilever file hosts for a system-document entry id + page.
 * Thumbnail / 6400px hosts are UUID5-derived when site + BQ document_id are
 * known — no Rolodex `entries/load` by `data.page_id`.
 */
export function useCantileverFileHostsForDocument(
  systemDocumentEntryId: string | undefined,
  siteId: string,
  pageNumber = 1,
  knownHosts?: KnownCantileverFileHosts
): {
  systemDocumentEntryId: string | undefined
  cantileverDocumentEntryId: string | undefined
  cantileverDocumentPageId: string | undefined
  filename: string | undefined
  bqDocumentId: string | undefined
  isLoading: boolean
} {
  const { isPipelineConfigScopeLoading } = usePipelineConfig()

  // Skip Rolodex doc load when hosts are already known from hydration, or when
  // we already have everything needed to derive UUID5 hosts + filename.
  const skipDocumentLookup =
    !!knownHosts?.hasHydratedDocumentHosts ||
    (!!knownHosts?.bqDocumentId &&
      (!!knownHosts?.filename || !!knownHosts?.cantileverDocumentEntryId))

  const entryIdForLookup =
    knownHosts?.systemDocumentEntryId ?? systemDocumentEntryId

  const {
    systemDocumentEntryId: resolvedSystemDocumentEntryId,
    cantileverDocumentEntryId: resolvedCantileverDocumentEntryId,
    filename: resolvedFilename,
    bqDocumentId: resolvedBqDocumentId,
    isLoading: isLoadingDoc,
  } = useSystemDocumentByEntryId(
    skipDocumentLookup || isPipelineConfigScopeLoading
      ? undefined
      : entryIdForLookup,
    siteId
  )

  const resolvedEntryId =
    knownHosts?.systemDocumentEntryId ??
    resolvedSystemDocumentEntryId ??
    systemDocumentEntryId

  const bqDocumentId = knownHosts?.bqDocumentId ?? resolvedBqDocumentId
  const filename = knownHosts?.filename ?? resolvedFilename

  const cantileverDocumentEntryId = useMemo(() => {
    if (knownHosts?.cantileverDocumentEntryId) {
      return knownHosts.cantileverDocumentEntryId
    }
    if (resolvedCantileverDocumentEntryId) {
      return resolvedCantileverDocumentEntryId
    }
    if (bqDocumentId) {
      return deriveCantileverDocumentEntryId(siteId, bqDocumentId)
    }
    return undefined
  }, [
    bqDocumentId,
    knownHosts?.cantileverDocumentEntryId,
    resolvedCantileverDocumentEntryId,
    siteId,
  ])

  const cantileverDocumentPageId = useMemo(() => {
    if (knownHosts?.cantileverDocumentPageId) {
      return knownHosts.cantileverDocumentPageId
    }
    if (bqDocumentId) {
      return cantileverDocumentPageIdForPage(siteId, bqDocumentId, pageNumber)
    }
    return undefined
  }, [bqDocumentId, knownHosts?.cantileverDocumentPageId, pageNumber, siteId])

  return {
    systemDocumentEntryId: resolvedEntryId,
    cantileverDocumentEntryId,
    cantileverDocumentPageId,
    filename,
    bqDocumentId,
    isLoading: !skipDocumentLookup && isLoadingDoc,
  }
}
